import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RosterReview } from "@/components/roster/RosterReview";
import { shiftColours, WEEKDAYS } from "@/components/roster/shiftColours";
import { DayChips, WeekGrid, OffRuleCard, monthWeeks } from "@/components/roster/WeekOffs";
import { formatTime12, shiftInfo, formatDateTimeIST } from "@/lib/format";
import { Wand2, Pin, Repeat, Hand, ChevronLeft, ChevronRight, Search, CopyPlus, AlertTriangle, Save } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const STEPS = ["Method", "Employees", "Shifts", "Week offs", "Review"];
const METHODS = [
  { id: "auto", icon: Wand2, title: "Auto-balance", text: "Fill each shift with the number of people it needs, sharing shifts fairly." },
  { id: "fixed", icon: Pin, title: "Fixed shift per employee", text: "Each person always works the same shift you pick for them." },
  { id: "rotation", icon: Repeat, title: "Weekly rotation", text: "Everyone rotates through the chosen shifts week by week." },
  { id: "manual", icon: Hand, title: "Manual", text: "Start with week offs only and assign shifts yourself on the grid." },
];
const ALL = "__all__";

function Stepper({ step, setStep, maxStep }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="wizard-stepper">
      {STEPS.map((s, i) => (
        <button key={s} disabled={i > maxStep} onClick={() => setStep(i)} data-testid={`wizard-step-${i}`}
          className={`flex items-center gap-2 rounded-full border-2 px-4 py-2 text-sm font-semibold transition-colors ${i === step ? "border-indigo-600 bg-indigo-600 text-white" : i <= maxStep ? "border-indigo-200 bg-white text-indigo-800 hover:bg-indigo-50" : "border-slate-200 bg-white text-slate-400"}`}>
          <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs ${i === step ? "bg-white text-indigo-700" : "bg-indigo-100 text-indigo-800"}`}>{i + 1}</span>{s}
        </button>
      ))}
    </div>
  );
}

function StepMethod({ method, setMethod }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {METHODS.map(({ id, icon: Icon, title, text }) => (
        <button key={id} onClick={() => setMethod(id)} data-testid={`method-${id}`}
          className={`rounded-xl border-2 p-5 text-left transition-colors ${method === id ? "border-indigo-600 bg-indigo-50" : "border-slate-200 bg-white hover:border-indigo-300"}`}>
          <Icon className={`mb-3 h-7 w-7 ${method === id ? "text-indigo-700" : "text-slate-500"}`} />
          <p className="text-lg font-bold text-slate-900">{title}</p>
          <p className="mt-1 text-sm text-slate-700">{text}</p>
        </button>
      ))}
    </div>
  );
}

function StepEmployees({ plan, masters, selected, setSelected }) {
  const { actingOrgId } = useAuth();
  const [f, setF] = useState({ search: "", department_id: plan.department_id || "", designation_id: "", location_id: plan.location_id || "" });
  const { data: raw = [], isLoading } = useQuery({ queryKey: ["wiz-emps", actingOrgId, f], queryFn: async () => (await api.get("/org/employees", { params: f })).data });
  const pl = plan.multi ? plan.location_ids || [] : [plan.location_id];
  const pd = plan.multi ? plan.department_ids || [] : [];
  const emps = raw.filter((e) => (!pl.length || pl.includes(e.location_id)) && (!pd.length || pd.includes(e.department_id)));
  const limit = (items, ids) => (ids.length ? items.filter((m) => ids.includes(m.id)) : items);
  const fixed = (label, items, id, testid) => (
    <span className="flex h-10 items-center rounded-md border border-slate-200 bg-slate-100 px-3 text-sm font-semibold text-slate-800" data-testid={testid}>
      {label}: {items.find((m) => m.id === id)?.name || "-"}
    </span>
  );
  const pick = (k, items, label) => (
    <Select value={f[k] || ALL} onValueChange={(v) => setF({ ...f, [k]: v === ALL ? "" : v })}>
      <SelectTrigger className="h-10 w-48 text-sm" data-testid={`wiz-filter-${k}`}><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value={ALL}>All {label}</SelectItem>{items.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
    </Select>
  );
  const allOn = emps.length > 0 && emps.every((e) => selected.has(e.id));
  const toggle = (id, v) => { const n = new Set(selected); v ? n.add(id) : n.delete(id); setSelected(n); };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <div className="relative min-w-[200px] flex-1"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input className="h-10 pl-9 text-sm" placeholder="Search name or code" value={f.search} onChange={(e) => setF({ ...f, search: e.target.value })} data-testid="wiz-search" /></div>
        {pick("department_id", limit(masters.departments, pd), "departments")}
        {pick("designation_id", masters.designations, "designations")}
        {plan.multi ? (pl.length === 1 ? fixed("Location", masters.locations, pl[0], "wiz-fixed-location") : pick("location_id", limit(masters.locations, pl), "locations")) : fixed("Location", masters.locations, plan.location_id, "wiz-fixed-location")}
      </div>
      <p className="text-sm font-semibold text-slate-800" data-testid="wiz-selected-count">{selected.size} employee(s) selected</p>
      <Card className="max-h-[420px] overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-slate-100 text-left text-slate-800"><tr>
            <th className="w-10 p-3"><Checkbox checked={allOn} onCheckedChange={(v) => { const n = new Set(selected); emps.forEach((e) => (v ? n.add(e.id) : n.delete(e.id))); setSelected(n); }} data-testid="wiz-select-all" /></th>
            <th className="p-3 font-semibold">Name</th><th className="p-3 font-semibold">Department</th><th className="p-3 font-semibold">Designation</th><th className="p-3 font-semibold">Location</th>
          </tr></thead>
          <tbody>{emps.map((e) => (
            <tr key={e.id} className="border-t border-slate-100 text-slate-800" data-testid={`wiz-emp-${e.id}`}>
              <td className="p-3"><Checkbox checked={selected.has(e.id)} onCheckedChange={(v) => toggle(e.id, v)} data-testid={`wiz-select-${e.id}`} /></td>
              <td className="p-3 font-medium">{e.name} <span className="text-slate-500">{e.employee_code}</span></td>
              <td className="p-3">{e.department_name || "-"}</td><td className="p-3">{e.designation_name || "-"}</td><td className="p-3">{e.location_name || "-"}</td>
            </tr>))}</tbody>
        </table>
        {!isLoading && !emps.length && <p className="p-8 text-center text-sm text-slate-500">No employees match these filters.</p>}
      </Card>
    </div>
  );
}

function StepShifts({ shifts, method, codes, setCodes, headcount, setHeadcount, holidayCount, setHolidayCount }) {
  const colours = shiftColours(shifts);
  if (!shifts.length) return <p className="text-base text-amber-700">No shifts exist for this location yet. An org admin creates them under "Shifts & rules".</p>;
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-700">Tick every shift this location or department runs.{method === "auto" && " Set how many people each shift needs per day."} "On holidays" is the minimum number kept working on each holiday from the Holidays page (for a department-only holiday, counted within that department). Everyone else gets H, taking turns.</p>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-slate-800" data-testid="wiz-shift-count">{codes.length} of {shifts.length} selected</span>
        <Button size="sm" variant="outline" disabled={codes.length === shifts.length} onClick={() => setCodes(shifts.map((s) => s.code))} data-testid="wiz-shift-select-all">Select all</Button>
        <Button size="sm" variant="outline" disabled={!codes.length} onClick={() => setCodes([])} data-testid="wiz-shift-deselect-all">Deselect all</Button>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {shifts.map((s) => {
          const on = codes.includes(s.code);
          return (
            <div key={s.code} className={`rounded-xl border-2 p-4 ${on ? "border-indigo-500 bg-white" : "border-slate-200 bg-slate-50"}`} data-testid={`wiz-shift-${s.code}`}>
              <label className="flex cursor-pointer items-center gap-3">
                <Checkbox checked={on} onCheckedChange={(v) => setCodes(v ? [...codes, s.code] : codes.filter((c) => c !== s.code))} data-testid={`wiz-shift-toggle-${s.code}`} />
                <span className="rounded-md px-2.5 py-1 text-base font-bold text-slate-900" style={{ background: colours[s.code] }}>{s.code}</span>
                <span><span className="block text-base font-semibold text-slate-900">{s.name}</span><span className="text-sm text-slate-700">{formatTime12(s.start)} – {formatTime12(s.end)} · <b>{shiftInfo(s.start, s.end)?.label}</b></span></span>
              </label>
              {on && method !== "manual" && (
                <div className="mt-3 grid grid-cols-2 gap-2 text-sm font-medium text-slate-800">
                  {method === "auto" && (
                    <label>People per day
                      <Input type="number" min={0} className="mt-1 h-9 text-base font-semibold" value={headcount[s.code] ?? 1}
                        onChange={(e) => setHeadcount({ ...headcount, [s.code]: Math.max(0, +e.target.value || 0) })} data-testid={`wiz-headcount-${s.code}`} />
                    </label>
                  )}
                  <label>On holidays
                    <Input type="number" min={0} className="mt-1 h-9 text-base font-semibold" value={holidayCount[s.code] ?? (method === "auto" ? headcount[s.code] ?? 1 : 0)}
                      onChange={(e) => setHolidayCount({ ...holidayCount, [s.code]: Math.max(0, +e.target.value || 0) })} data-testid={`wiz-holiday-headcount-${s.code}`} />
                  </label>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OffsFilter({ employees, value, onChange }) {
  const opts = (k, nk) => [...new Map(employees.filter((e) => e[k]).map((e) => [e[k], e[nk] || e[k]])).entries()];
  const sel = (k, nk, label) => (
    <Select value={value[k] || ALL} onValueChange={(v) => onChange({ ...value, [k]: v === ALL ? "" : v })}>
      <SelectTrigger className="h-9 w-44 text-sm" data-testid={`offs-filter-${k}`}><SelectValue /></SelectTrigger>
      <SelectContent><SelectItem value={ALL}>All {label}</SelectItem>{opts(k, nk).map(([id, n]) => <SelectItem key={id} value={id}>{n}</SelectItem>)}</SelectContent>
    </Select>
  );
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="offs-filter">
      <div className="relative min-w-[180px] flex-1"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input className="h-9 pl-9 text-sm" placeholder="Search name or code" value={value.search} onChange={(e) => onChange({ ...value, search: e.target.value })} data-testid="offs-search" /></div>
      {sel("location_id", "location_name", "locations")}
      {sel("department_id", "department_name", "departments")}
      {sel("designation_id", "designation_name", "designations")}
    </div>
  );
}

function StepOffs({ plan, employees: allEmps, method, codes, inputs, setInputs, offRule, setOffRule, offMode, setOffMode }) {
  const [flt, setFlt] = useState({ search: "", location_id: "", department_id: "", designation_id: "" });
  const employees = allEmps.filter((e) => ["location_id", "department_id", "designation_id"].every((k) => !flt[k] || e[k] === flt[k])
    && (!flt.search || `${e.name} ${e.employee_code || ""}`.toLowerCase().includes(flt.search.toLowerCase())));
  const set = (id, patch) => setInputs({ ...inputs, [id]: { ...(inputs[id] || {}), ...patch } });
  const weeks = monthWeeks(plan.year, plan.month);
  const manual = method === "manual";
  const mode = manual ? "pick" : offMode;
  const spread = () => setInputs({ ...inputs, ...Object.fromEntries(employees.map((e, i) => [e.id, { ...(inputs[e.id] || {}), fixed_shift: codes[i % codes.length] }])) });
  const MODES = [["auto", "Set week offs automatically", "Pick a rule and the system spreads week offs for everyone."],
    ["pick", "I'll pick week offs myself", "Choose the off day(s) for each person."]];
  return (
    <div className="space-y-4">
      {!manual && (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="off-mode-choice">
          {MODES.map(([k, t, d]) => (
            <button key={k} onClick={() => setOffMode(k)} data-testid={`off-mode-${k}`}
              className={`rounded-xl border-2 p-4 text-left transition-colors ${mode === k ? "border-indigo-600 bg-indigo-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
              <p className={`text-base font-bold ${mode === k ? "text-indigo-800" : "text-slate-900"}`}>{t}</p>
              <p className="text-sm text-slate-600">{d}</p>
            </button>
          ))}
        </div>
      )}
      {manual && <p className="rounded-lg bg-indigo-50 px-4 py-3 text-sm text-indigo-900" data-testid="manual-offs-note">Pick each person's week off. Next, the month grid opens with holidays, leave and these week offs already filled in, and you fill the shifts yourself.</p>}
      {method === "fixed" && <Button size="sm" variant="outline" onClick={spread} data-testid="spread-shifts">Spread shifts evenly</Button>}
      {mode === "auto" && <OffRuleCard rule={offRule.type === "none" ? { ...offRule, type: "location" } : offRule} setRule={setOffRule} weeks={weeks} month={plan.month} />}
      {(mode === "pick" || method === "fixed") && (
        <>
        <OffsFilter employees={allEmps} value={flt} onChange={setFlt} />
        <p className="text-sm text-slate-600" data-testid="offs-showing">Showing {employees.length} of {allEmps.length} selected. Changes you make stay when you switch the filter; “Copy this to everyone” copies to the people shown.</p>
        <Card className="divide-y divide-slate-100">
          {employees.map((e, i) => <OffsRow key={e.id} e={e} first={i === 0} inp={inputs[e.id] || {}} set={(p) => set(e.id, p)} pick={mode === "pick"} shown={employees.length}
            method={method} codes={codes} weeks={weeks} month={plan.month} onCopyAll={(p) => setInputs({ ...inputs, ...Object.fromEntries(employees.map((x) => [x.id, { ...(inputs[x.id] || {}), ...p }])) })} />)}
          {!employees.length && <p className="p-6 text-center text-sm text-slate-500">{allEmps.length ? "No one matches this filter." : "No employees selected."}</p>}
        </Card>
        </>
      )}
    </div>
  );
}

function OffsRow({ e, inp, set, pick, method, codes, weeks, month, onCopyAll, shown }) {
  const perWeek = inp.off_style ? inp.off_style === "week" : Object.keys(inp.week_offs || {}).length > 0;
  const none = !(inp.off_days || []).length && !Object.values(inp.week_offs || {}).some((v) => v.length);
  const copyAll = () => {
    if (window.confirm(`Copy ${e.name}'s week offs to the ${shown} people shown? This replaces their current picks.`)) {
      onCopyAll({ off_style: perWeek ? "week" : "same", off_days: perWeek ? [] : inp.off_days || [], week_offs: perWeek ? inp.week_offs || {} : {} });
    }
  };
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-testid={`offs-row-${e.id}`}>
      <div className="min-w-[160px]"><p className="text-base font-semibold text-slate-900">{e.name}</p><p className="text-sm text-slate-600">{[e.designation_name, e.department_name].filter(Boolean).join(" · ")}</p>
        {pick && none && <p className="text-xs font-semibold text-amber-700" data-testid={`no-off-${e.id}`}>No week off picked</p>}</div>
      <div className="flex flex-wrap items-center gap-3">
        {method === "fixed" && (
          <Select value={inp.fixed_shift || ""} onValueChange={(v) => set({ fixed_shift: v })}>
            <SelectTrigger className="h-9 w-32 text-sm" data-testid={`fixed-shift-${e.id}`}><SelectValue placeholder="Shift" /></SelectTrigger>
            <SelectContent>{codes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {pick && (
          <div className="inline-flex rounded-lg bg-slate-100 p-1" data-testid={`week-mode-${e.id}`}>
            {[["same", "Same day every week"], ["week", "Different day each week"]].map(([k, l]) => (
              <button key={k} onClick={() => set({ off_style: k })} data-testid={`week-mode-${e.id}-${k}`}
                className={`rounded-md px-3 py-1.5 text-sm font-semibold ${(perWeek ? "week" : "same") === k ? "bg-white text-indigo-700 shadow-sm" : "text-slate-600"}`}>{l}</button>
            ))}
          </div>
        )}
        {pick && !perWeek && <DayChips value={inp.off_days || []} onChange={(v) => set({ off_days: v })} testid={`offs-${e.id}`} />}
        {pick && <button onClick={copyAll} className="text-xs font-semibold text-indigo-700 underline" data-testid={`copy-offs-all-${e.id}`}>Copy this to everyone</button>}
      </div>
      {pick && perWeek && (
        <div className="w-full rounded-lg bg-slate-50 p-3">
          <WeekGrid value={inp.week_offs || {}} onChange={(v) => set({ week_offs: v })} weeks={weeks} month={month} testid={`week-offs-${e.id}`} />
        </div>
      )}
    </div>
  );
}

function CopyPrevious({ pid, info, onCopied }) {
  const [busy, setBusy] = useState(false);
  const name = `${MONTHS[info.month - 1]} ${info.year}`;
  const copy = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/roster/plans/${pid}/copy-previous`);
      toast.success(`Copied ${data.copied_employees} employee(s) from ${name}. Review and adjust.`);
      onCopied(data.plan);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Card className="flex flex-wrap items-center justify-between gap-4 border-2 border-indigo-300 bg-indigo-50 p-5" data-testid="copy-previous-card">
      <div className="max-w-2xl">
        <p className="text-base font-bold text-indigo-950">{name} roster found ({info.status === "approved" ? "approved" : info.status}, {info.employee_count} employees)</p>
        <p className="mt-1 text-sm text-indigo-900">Reuse its employees, shifts, week offs and pattern. Days are matched by weekday: the 1st Monday copies the 1st Monday, and an extra 5th weekday copies the 4th. Leave isn't copied, this month's holidays are applied, and rotations continue from where they stopped.</p>
      </div>
      <Button size="lg" disabled={busy} onClick={copy} data-testid="copy-previous-btn"><CopyPlus className="mr-2 h-4 w-4" /> {busy ? "Copying…" : `Copy from ${MONTHS[info.month - 1]}`}</Button>
    </Card>
  );
}

function ShiftClashWarning({ clashes }) {
  if (!clashes.length) return null;
  return (
    <div className="mb-5 rounded-xl border-2 border-amber-400 bg-amber-50 p-4 text-amber-950" role="alert" data-testid="shift-clash-warning">
      <p className="flex items-center gap-2 text-base font-bold"><AlertTriangle className="h-5 w-5" /> Same shift code, different timings</p>
      <p className="mb-2 text-sm">This roster uses one timing per code for everyone, so some people may get the wrong hours. Rename one of them in Shifts &amp; rules, or use a shared shift.</p>
      {clashes.map((c) => (
        <p key={c.code} className="text-sm" data-testid={`shift-clash-${c.code}`}>
          <b>{c.code}</b>: {c.variants.map((v) => `${v.location} ${formatTime12(v.start)}–${formatTime12(v.end)}${v.used ? " (used)" : ""}`).join(" · ")}
        </p>
      ))}
    </div>
  );
}

export function RosterWizard({ plan: initialPlan, onExit }) {
  const { actingOrgId } = useAuth();
  const [board, setBoard] = useState(null);
  const [step, setStep] = useState(0);
  const [maxStep, setMaxStep] = useState(0);
  const [method, setMethod] = useState(initialPlan.method || "auto");
  const [selected, setSelected] = useState(new Set());
  const [codes, setCodes] = useState(initialPlan.shift_codes || []);
  const [headcount, setHeadcount] = useState(Object.fromEntries(Object.entries(initialPlan.coverage || {}).map(([k, v]) => [k.split("|")[0], v.weekday])));
  const [holidayCount, setHolidayCount] = useState(Object.fromEntries(Object.entries(initialPlan.coverage || {}).map(([k, v]) => [k.split("|")[0], v.holiday])));
  const [inputs, setInputs] = useState(initialPlan.employee_inputs || {});
  const [offRule, setOffRule] = useState(initialPlan.off_rule || { type: "location", n: 1, pattern: {} });
  const [offMode, setOffMode] = useState(() => initialPlan.off_mode || (Object.values(initialPlan.employee_inputs || {}).some((x) => (x.off_days || []).length || Object.keys(x.week_offs || {}).length) ? "pick" : "auto"));
  const [busy, setBusy] = useState(false);
  const [prevInfo, setPrevInfo] = useState(null);
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const pid = initialPlan.id;

  const { data: shifts = [] } = useQuery({ queryKey: ["wiz-shifts", actingOrgId, initialPlan.id], queryFn: async () => (await api.get(`/roster/shifts?plan_id=${initialPlan.id}`)).data });
  const { data: masters = { departments: [], designations: [], locations: [] } } = useQuery({
    queryKey: ["r-masters", actingOrgId],
    queryFn: async () => {
      const [d, g, l] = await Promise.all(["departments", "designations", "locations"].map((m) => api.get(`/org/masters/${m}`)));
      return { departments: d.data, designations: g.data, locations: l.data };
    },
  });
  const load = async () => { const { data } = await api.get(`/roster/plans/${pid}/board`); setBoard(data); return data; };

  useEffect(() => {
    load().then((b) => {
      setSelected(new Set(b.employees.map((e) => e.id)));
      const p = b.plan;
      const cov = p.coverage || {};
      const hasPicks = Object.values(p.employee_inputs || {}).some((x) => (x.off_days || []).length || Object.keys(x.week_offs || {}).length);
      setInputs(p.employee_inputs || {});
      setHeadcount(Object.fromEntries(Object.entries(cov).map(([k, v]) => [k.split("|")[0], v.weekday])));
      setHolidayCount(Object.fromEntries(Object.entries(cov).map(([k, v]) => [k.split("|")[0], v.holiday])));
      if (p.off_rule) setOffRule(p.off_rule);
      setOffMode(p.off_mode || (hasPicks ? "pick" : "auto"));
      if (b.can_edit === false || b.has_draft || (b.plan.status && b.plan.status !== "draft")) { setStep(4); setMaxStep(4); }
      else {
        const d = b.plan.wizard_draft;
        if (d?.state) {
          const s = d.state;
          if (s.method) setMethod(s.method);
          if (s.codes) setCodes(s.codes);
          if (s.headcount) setHeadcount(s.headcount);
          if (s.holidayCount) setHolidayCount(s.holidayCount);
          if (s.inputs) setInputs(s.inputs);
          if (s.offRule) setOffRule(s.offRule);
          if (s.offMode) setOffMode(s.offMode);
          if (s.selected?.length) { const ok = new Set(b.employees.map((e) => e.id)); setSelected(new Set(s.selected.filter((id) => ok.has(id)))); }
          setStep(d.step || 0); setMaxStep(d.step || 0); setSavedAt(d.saved_at);
        }
        api.get(`/roster/plans/${pid}/previous`).then((r) => setPrevInfo(r.data.found ? r.data : null));
      }
    }).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [pid]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!codes.length && shifts.length && !initialPlan.shift_codes?.length) setCodes(shifts.map((s) => s.code)); }, [shifts]); // eslint-disable-line react-hooks/exhaustive-deps

  const go = (n) => { setStep(n); setMaxStep((m) => Math.max(m, n)); };
  const saveEmployees = async () => {
    const cur = new Set(board.employees.map((e) => e.id));
    const add = [...selected].filter((id) => !cur.has(id));
    const remove = [...cur].filter((id) => !selected.has(id));
    if (add.length || remove.length) { await api.post(`/roster/plans/${pid}/employees`, { add, remove }); await load(); }
  };
  const generate = async () => {
    setBusy(true);
    try {
      const holiday_headcount = Object.fromEntries(codes.map((c) => [c, holidayCount[c] ?? (method === "auto" ? headcount[c] ?? 1 : 0)]));
      const mode = method === "manual" ? "pick" : offMode;
      const clean = Object.fromEntries(Object.entries(inputs).map(([id, x]) => {
        const week = x.off_style ? x.off_style === "week" : Object.keys(x.week_offs || {}).length > 0;
        return [id, { fixed_shift: x.fixed_shift, off_days: week ? [] : x.off_days || [], week_offs: week ? x.week_offs || {} : {} }];
      }));
      const sentHeadcount = Object.fromEntries(codes.map((c) => [c, headcount[c] ?? 1]));
      await api.put(`/roster/plans/${pid}/setup`, { method, shift_codes: codes, headcount: sentHeadcount, holiday_headcount, employee_inputs: clean, off_rule: offRule.type === "none" ? { ...offRule, type: "location" } : offRule, off_mode: mode });
      const { data } = await api.post(`/roster/plans/${pid}/generate`);
      toast.success(method === "manual" ? "Grid ready: holidays, leave and week offs are filled in" : data.violations ? `Roster generated with ${data.violations} rule warning(s)` : "Roster generated");
      await load(); go(4);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const draftState = () => ({ method, codes, headcount, holidayCount, inputs, offRule, offMode, selected: [...selected] });
  const saveDraft = async (exit) => {
    setSaving(true);
    try {
      if (step >= 1 && selected.size) await saveEmployees();
      const { data } = await api.put(`/roster/plans/${pid}/wizard-draft`, { step, state: draftState() });
      setSavedAt(data.saved_at);
      toast.success("Progress saved as draft. Open this roster again any time to continue.");
      if (exit) onExit();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const next = async () => {
    try {
      if (step === 1) { if (!selected.size) return toast.error("Select at least one employee"); await saveEmployees(); }
      if (step === 2 && method !== "manual" && !codes.length) return toast.error("Choose at least one shift");
      if (step === 3) return generate();
      go(step + 1);
      api.put(`/roster/plans/${pid}/wizard-draft`, { step: step + 1, state: draftState() }).then((r) => setSavedAt(r.data.saved_at)).catch(() => {});
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const onCopied = async (p) => {
    setMethod(p.method || "auto"); setCodes(p.shift_codes || []); setInputs(p.employee_inputs || {}); setOffRule(p.off_rule || { type: "location", n: 1, pattern: {} }); if (p.off_mode) setOffMode(p.off_mode);
    setHeadcount(Object.fromEntries(Object.entries(p.coverage || {}).map(([k, v]) => [k.split("|")[0], v.weekday])));
    setHolidayCount(Object.fromEntries(Object.entries(p.coverage || {}).map(([k, v]) => [k.split("|")[0], v.holiday])));
    const b = await load(); setSelected(new Set(b.employees.map((e) => e.id))); setPrevInfo(null); go(4);
  };

  if (!board) return <p className="text-base text-slate-600">Loading roster…</p>;
  const names = (ids, list) => ids.map((id) => list.find((x) => x.id === id)?.name).filter(Boolean).join(", ");
  const locName = initialPlan.pick ? "Picked employees" : initialPlan.multi ? (names(initialPlan.location_ids || [], masters.locations) || "All locations") : masters.locations.find((l) => l.id === initialPlan.location_id)?.name;
  const deptName = initialPlan.pick ? "" : initialPlan.multi ? (names(initialPlan.department_ids || [], masters.departments) || "All departments") : masters.departments.find((d) => d.id === initialPlan.department_id)?.name;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-2xl font-bold text-slate-900" data-testid="wizard-title">{locName}{deptName ? ` · ${deptName}` : ""} — {MONTHS[initialPlan.month - 1]} {initialPlan.year}</p>
          <p className="text-sm text-slate-600">{board.employees.length} employee(s) · method: {METHODS.find((m) => m.id === method)?.title}</p>
        </div>
        <Button variant="outline" onClick={onExit} data-testid="wizard-exit">Change roster</Button>
      </div>
      {board.can_edit !== false && board.plan.status !== "submitted" && <Stepper step={step} setStep={setStep} maxStep={maxStep} />}
      <ShiftClashWarning clashes={board.shift_clashes || []} />
      {prevInfo && step < 4 && <CopyPrevious pid={pid} info={prevInfo} onCopied={onCopied} />}
      {step === 0 && <StepMethod method={method} setMethod={setMethod} />}
      {step === 1 && <StepEmployees plan={initialPlan} masters={masters} selected={selected} setSelected={setSelected} />}
      {step === 2 && <StepShifts shifts={shifts} method={method} codes={codes} setCodes={setCodes} headcount={headcount} setHeadcount={setHeadcount} holidayCount={holidayCount} setHolidayCount={setHolidayCount} />}
      {step === 3 && <StepOffs plan={initialPlan} employees={board.employees} method={method} codes={codes} inputs={inputs} setInputs={setInputs} offRule={offRule} setOffRule={setOffRule} offMode={offMode} setOffMode={setOffMode} />}
      {step === 4 && <RosterReview board={board} shifts={shifts} masters={masters} onChanged={load} onRegenerate={() => go(0)} />}
      {step < 4 && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="wizard-back"><ChevronLeft className="mr-1 h-4 w-4" /> Back</Button>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {savedAt && <span className="text-xs text-slate-500" data-testid="wizard-saved-at">Saved {formatDateTimeIST(savedAt)}</span>}
            <Button variant="outline" disabled={saving || busy} onClick={() => saveDraft(false)} data-testid="wizard-save-draft"><Save className="mr-1 h-4 w-4" /> Save draft</Button>
            <Button variant="ghost" disabled={saving || busy} onClick={() => saveDraft(true)} data-testid="wizard-save-exit">Save &amp; exit</Button>
            <Button size="lg" disabled={busy} onClick={next} data-testid="wizard-next">
              {step === 3 ? (busy ? "Preparing…" : method === "manual" ? "Open roster grid" : "Generate roster") : "Next"} <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
